import React, { useState } from 'react';
import { Webhook, Key, Save, Loader2, CheckCircle, AlertTriangle, RefreshCw, RotateCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { CopyButton } from './CopyButton';

const formatWhen = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('pt-PT'); } catch { return iso.slice(0, 19).replace('T', ' '); }
};

const inBacklog = (log) =>
  !log.success && (log.retry_count >= 3 || (log.retry_count > 0 && !log.next_retry_at));

const WebhookLogRow = ({ log, onRedeliver, busy }) => {
  const isFailed = !log.success;
  const exhausted = isFailed && log.retry_count > 0 && !log.next_retry_at;
  const struggling = isFailed && log.retry_count >= 3 && !!log.next_retry_at;

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-2 p-3 rounded border text-sm ${
        exhausted ? 'bg-red-50 border-red-200'
        : struggling ? 'bg-amber-50 border-amber-200'
        : isFailed ? 'bg-slate-50 border-slate-200'
        : 'bg-white border-slate-200'
      }`}
      data-testid={`webhook-log-row-${log.log_id || log.id}`}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {log.success
          ? <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
          : <AlertTriangle className={`w-4 h-4 shrink-0 ${exhausted ? 'text-red-600' : 'text-amber-600'}`} />}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-slate-800 font-mono text-xs truncate">{log.event}</span>
            {exhausted && (
              <Badge className="bg-red-100 text-red-700 border border-red-200 text-[10px]">
                Esgotado ({log.retry_count})
              </Badge>
            )}
            {struggling && (
              <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px]">
                Retry {log.retry_count}/5
              </Badge>
            )}
            {isFailed && !exhausted && !struggling && log.retry_count > 0 && (
              <Badge className="bg-slate-100 text-slate-600 border border-slate-200 text-[10px]">
                Retry {log.retry_count}/5
              </Badge>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {log.success
              ? <>HTTP {log.status_code} · {formatWhen(log.created_at)}</>
              : <>{log.status_code ? `HTTP ${log.status_code}` : (log.error || 'falhou')} · {formatWhen(log.created_at)}
                {log.next_retry_at && <> · próxima tentativa {formatWhen(log.next_retry_at)}</>}
              </>}
          </div>
        </div>
      </div>
      {isFailed && (
        <Button
          size="sm" variant="outline"
          className="h-8 border-slate-300 text-slate-700 hover:bg-white shrink-0"
          onClick={() => onRedeliver(log)}
          disabled={busy}
          data-testid={`redeliver-webhook-btn-${log.log_id || log.id}`}
        >
          {busy
            ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
          Reenviar
        </Button>
      )}
    </div>
  );
};

export const WebhookTab = ({
  webhookUrl, setWebhookUrl, webhookEnabled, setWebhookEnabled,
  webhookSecret, savingWebhook, onSaveWebhook, webhookLogs, onReloadLogs,
}) => {
  const [busyLogId, setBusyLogId] = useState('');
  const [redeliverAll, setRedeliverAll] = useState(false);

  const handleRedeliver = async (log) => {
    const logId = log.log_id;
    if (!logId) {
      toast.error('Este log é antigo e não tem ID — refresque a página.');
      return;
    }
    setBusyLogId(logId);
    try {
      const res = await api.post(`/super-admin/webhook-logs/${logId}/redeliver`);
      if (res.data?.success) {
        toast.success(`Reentregue com sucesso (HTTP ${res.data.status_code})`);
      } else {
        toast.error(res.data?.message || 'Reentrega falhou — o Hub continua indisponível.');
      }
      onReloadLogs?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na reentrega');
    } finally {
      setBusyLogId('');
    }
  };

  const handleRedeliverAll = async () => {
    setRedeliverAll(true);
    try {
      const res = await api.post('/super-admin/webhook-logs/redeliver-all-failed');
      toast.success(`Tentativas: ${res.data.attempted} · Entregues: ${res.data.delivered} · Ainda a falhar: ${res.data.still_failing}`);
      onReloadLogs?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na reentrega em lote');
    } finally {
      setRedeliverAll(false);
    }
  };

  const failedCount = webhookLogs.filter(l => !l.success).length;
  const backlogCount = webhookLogs.filter(inBacklog).length;

  return (
    <Card className="bg-white border-slate-200">
      <CardHeader>
        <CardTitle className="text-slate-900 flex items-center gap-2">
          <Webhook className="w-5 h-5 text-indigo-700" /> Webhooks de Estado
        </CardTitle>
        <CardDescription className="text-slate-500">
          A COGNIEtica notifica o CogniHub em cada alteração de estado dos clientes
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label className="text-slate-600">URL de callback do CogniHub</Label>
          <Input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://cognihub.exemplo.pt/webhooks/cognietica"
            className="bg-white border-slate-300 text-slate-900" data-testid="webhook-url-input" />
        </div>

        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
          <div>
            <Label className="text-slate-900">Webhooks ativos</Label>
            <p className="text-sm text-slate-500 mt-1">Ativar o envio de eventos para o CogniHub</p>
          </div>
          <Switch checked={webhookEnabled} onCheckedChange={setWebhookEnabled} data-testid="webhook-enabled-toggle" />
        </div>

        {webhookSecret && (
          <Alert className="bg-amber-900/20 border-indigo-200">
            <Key className="w-4 h-4 text-indigo-700" />
            <AlertDescription className="text-amber-800">
              <p className="font-medium mb-1">Segredo de assinatura (guarde no CogniHub):</p>
              <div className="flex items-center gap-2">
                <code className="text-xs break-all">{webhookSecret}</code>
                <CopyButton value={webhookSecret} />
              </div>
            </AlertDescription>
          </Alert>
        )}

        <div className="text-xs text-slate-500 space-y-1">
          <p>Eventos: <code className="text-indigo-700">tenant.*</code>, <code className="text-indigo-700">tenant.user.*</code>, <code className="text-indigo-700">tenant.settings.email_config_changed</code>, <code className="text-indigo-700">tenant.attachment.uploaded</code></p>
          <p>Retries automáticos: 1m, 5m, 20m, 1h, 4h (backoff exponencial)</p>
          <p>Assinatura HMAC-SHA256 no header <code className="text-indigo-700">X-Cognietica-Signature</code></p>
        </div>

        <div className="flex justify-end">
          <Button onClick={onSaveWebhook} disabled={savingWebhook}
            className="bg-indigo-700 hover:bg-amber-600 text-black" data-testid="save-webhook-btn">
            {savingWebhook ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar Webhook
          </Button>
        </div>

        <Separator className="bg-slate-700" />

        <div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h3 className="text-slate-900 font-medium">Histórico de entregas</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {webhookLogs.length} registo(s)
                {failedCount > 0 && <> · <span className="text-red-600 font-medium">{failedCount} a falhar</span></>}
                {backlogCount > 0 && <> · <span className="text-amber-700 font-medium">{backlogCount} em backlog</span></>}
              </p>
            </div>
            {failedCount > 0 && (
              <Button
                size="sm" variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={handleRedeliverAll}
                disabled={redeliverAll}
                data-testid="redeliver-all-webhooks-btn"
              >
                {redeliverAll
                  ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <RotateCw className="w-3.5 h-3.5 mr-1.5" />}
                Reenviar todas as falhadas
              </Button>
            )}
          </div>
          {webhookLogs.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="webhook-logs-empty">Ainda não há entregas registadas.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="webhook-logs-list">
              {webhookLogs.map((log) => (
                <WebhookLogRow
                  key={log.log_id || log.id || `${log.event}-${log.created_at}`}
                  log={log}
                  onRedeliver={handleRedeliver}
                  busy={busyLogId === log.log_id}
                />
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
